import { IconMap } from './Icons.js';

/**
 * Map thumbnail placeholder. RustMaps doesn't expose a public thumbnail URL
 * for arbitrary map ids — their CDN paths require an API key and 401 / 404
 * for direct img tag fetches. Until we wire up a backend proxy with an API
 * key, render a deterministic colored placeholder so each queue entry is
 * still visually distinct without lighting up the dev tools console.
 *
 * The hue is seeded from the URL so the same map always renders the same
 * colors across reloads and across operators.
 */
interface Props {
  url: string;
  size?: number;
  className?: string;
}

function colorFromUrl(url: string): { c1: string; c2: string } {
  let hash = 0;
  for (let i = 0; i < url.length; i++) {
    hash = (hash << 5) - hash + url.charCodeAt(i);
    hash |= 0;
  }
  const hue = Math.abs(hash) % 360;
  return {
    c1: `hsl(${hue}, 35%, 24%)`,
    c2: `hsl(${(hue + 40) % 360}, 30%, 14%)`,
  };
}

export function MapPreview({ url, size = 56, className = '' }: Props) {
  const { c1, c2 } = colorFromUrl(url);
  return (
    <div
      className={`rounded-md border border-bg-border flex items-center justify-center text-fg-dim ${className}`}
      style={{
        width: size,
        height: size,
        background: `radial-gradient(circle at 30% 30%, ${c1}, ${c2})`,
      }}
      aria-hidden
    >
      <IconMap className="w-5 h-5 opacity-70" />
    </div>
  );
}
